import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';

import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout, filterFormItemStyle, computeButtonColLayout } from '@utils/property';
import { Form, Row, Col, Input, Select, Space, Button, Table, Tooltip, message, Cascader, Typography, Dropdown, Popover, Divider, Card, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import AddOrgDrawer from '../components/AddOrgDrawer';
import ViewOrgDrawer from '../components/ViewOrgDrawer';
import DataOverviewDrawer from '../components/DataOverviewDrawer';
import copy from 'copy-to-clipboard';
import './index.less';
import { medTypeList, medOwnershipList, medLevelList, medGradeList, orgTypeList } from '@/utils/constants';
import { regionOptions } from '@/utils/region';
import { organizationsListApi, organizationsGroupCountApi } from '@/services/DC/Organization';
import { organizationSliceActions } from '../store/organizationSlice';
import { expertActions } from '@/components/Expert/overview/store/expertSlice';
import { useDispatch, useSelector } from 'react-redux';
import config from '@config';
import { prepareBCOSSUrl, formatEstablishedTime } from '@/utils/utils';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
const { Text, Link } = Typography;

const { setOrganizationFilter: setFilter, setOrganizationCursor: setCursor } = organizationSliceActions;
const { setFilter: setExpertFilter, setCursor: setExpertCursor } = expertActions; // 专家库列表筛选

const OrganizationList = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { token } = theme.useToken();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.organization?.organization?.filter || { page: 1, size: 20 });
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();
    const cursor = useSelector(state => state.organization?.organization?.cursor || 0);

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [currentOrg, setCurrentOrg] = useState(undefined);

    const [openAddOrgDrawer, setOpenAddOrgDrawer] = useState(false); // 新增drawer
    const [openViewOrgDrawer, setOpenViewOrgDrawer] = useState(false); // 查看drawer
    const [currentRecordId, setCurrentRecordId] = useState(undefined);
    const [selectedOrgsIds, setSelectedOrgsIds] = useState([]);
    const [groupCount, setGroupCount] = useState({});

    const [openDataOverviewDrawer, setOpenDataOverviewDrawer] = useState(false);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const orgType = Form.useWatch('orgType', form);
    const established = Form.useWatch('established', form);

    // 动态计算筛选字段数：基础 6（含机构特色） + 医疗机构相关 4
    const filterFieldCount = orgType?.includes(1) ? 10 : 6;
    const buttonColLayout = computeButtonColLayout(filterFieldCount);

    // 查看
    const handleView = record => {
        setOpenViewOrgDrawer(true);
        setCurrentRecordId(record.id);
        dispatch(setCursor(record.id));
    };

    // 编辑
    const handleEdit = record => {
        setOpenAddOrgDrawer(true);
        setCurrentRecordId(record.id);
        dispatch(setCursor(record.id));
    };

    // 新增机构
    const handleAddOrg = () => {
        setOpenAddOrgDrawer(true);
        setCurrentRecordId(null);
    };

    // 准备医疗机构级别和等级信息
    // - 级别为三级、二级、一级时，显示级别；等次为甲等、乙等、丙等时，显示等次
    // - 级别和等次均显示时，简化显示：三级甲等 -> 三甲，二级甲等 -> 二甲，一级甲等 -> 一甲
    // - 级别和等次有其一时，不简化
    const prepareMedLevelGrade = unit => {
        let levelWithGrade = '';
        const level = unit?.medLevel;
        const grade = unit?.medGrade;

        // 判断是否有有效的级别和等次
        const hasValidLevel = level && (level === '三级' || level === '二级' || level === '一级');
        const hasValidGrade = grade && (grade === '甲等' || grade === '乙等' || grade === '丙等');

        if (hasValidLevel && hasValidGrade) {
            // 级别和等次均有时，简化显示：三级甲等 -> 三甲
            if (grade === '甲等') {
                levelWithGrade = `${level.replace('级', '')}甲`;
            } else if (grade === '乙等') {
                levelWithGrade = `${level.replace('级', '')}乙`;
            } else if (grade === '丙等') {
                levelWithGrade = `${level.replace('级', '')}丙`;
            }
        } else if (hasValidLevel) {
            // 只有级别时，不简化，直接显示级别
            levelWithGrade = level;
        } else if (hasValidGrade) {
            // 只有等次时，也不简化，直接显示等次
            levelWithGrade = grade;
        }

        return levelWithGrade;
    };

    const onFinish = values => {
        setSelectedOrgsIds([]); // 重置已选择的机构ID列表
        const newValues = { ...values };
        newValues.orgType = newValues.orgType?.length ? newValues.orgType : undefined;
        // 所在地区 - 拆分保存
        if (values.areas && values.areas.length > 0) {
            newValues.provinceCode = values.areas[0];
            newValues.cityCode = values.areas[1];
            newValues.districtCode = values.areas[2];
        } else {
            newValues.provinceCode = undefined;
            newValues.cityCode = undefined;
            newValues.districtCode = undefined;
        }
        if (values.established === 0) {
            newValues.established = values.year;
        }

        if (values.keywordList) {
            newValues.keywordList = values.keywordList.split(',').filter(item => item?.replace(/(^[ {2}]+|[ {2}]+$)/g, ''));
        } else {
            newValues.keywordList = undefined;
        }

        if (values.featureList) {
            newValues.featureList = values.featureList.split(',').filter(item => item?.replace(/(^[ {2}]+|[ {2}]+$)/g, ''));
        } else {
            newValues.featureList = undefined;
        }

        delete newValues.areas;
        delete newValues.year;
        dispatch(setFilter({ ...filter, ...newValues, page: 1 }));
    };

    // 重置
    const onReset = () => {
        form.resetFields();
        dispatch(setFilter({ page: 1, size: 20 }));
    };

    // 刷新
    const handleRefresh = () => {
        getOrganizationsList();
    };

    const onChangePage = (page, size) => {
        dispatch(setFilter({ ...filter, page, size }));
    };

    const onShowSizeChange = (_, size) => {
        dispatch(setFilter({ ...filter, size }));
    };

    // 处理所在地区
    const dealArea = row => {
        if (/^北京|上海|天津|重庆/.test(row.province)) {
            return `${row.city}${row.district}`;
        } else {
            return `${row.province}${row.city}${row.district}`;
        }
    };

    // 处理周年
    const dealYear = year => {
        // 4 或 9 的余数，则展示将满周年
        if ((parseInt(year, 10) + 1) % 5 === 0) {
            return (
                <>
                    {year}周年<Text type="warning">(将满{year + 1}周年)</Text>
                </>
            );
        } else {
            return <>{year}周年</>;
        }
    };

    // 准备机构别称
    const prepareOrgAliases = org => {
        if (!org?.aliases) return '';

        try {
            // 解析JSON格式的别称数据
            const aliases = JSON.parse(org.aliases);
            if (!Array.isArray(aliases) || aliases.length === 0) return '';

            // 分离当前别称和曾用名
            const currentAliases = aliases.filter(alias => !alias.former);
            const formerAliases = aliases.filter(alias => alias.former);

            let aliasText = '';

            // 添加当前别称
            if (currentAliases.length > 0) {
                const currentNames = currentAliases.map(alias => alias.name).join('、');
                aliasText += currentNames;
            }

            // 添加曾用名
            if (formerAliases.length > 0) {
                const formerNames = formerAliases.map(alias => `${alias.name}(曾用名)`).join('、');
                if (!!aliasText) aliasText += '、';
                aliasText += formerNames;
            }

            return aliasText;
        } catch (error) {
            console.error('解析机构别称失败:', error);
            return '';
        }
    };

    const onCopy = msg => {
        copy(msg);
        message.success('已复制到剪贴板');
    };

    // 准备logo url，兼容用户中心上传的 upload 打头的 logo url
    const prepareLogoUrl = logo => {
        let url = '';

        if (!!logo && typeof logo === 'string') {
            if (logo.startsWith('upload/')) {
                url = `${config.FILE_PREFIX}/${logo}`;
            } else {
                url = prepareBCOSSUrl(logo);
            }
        }

        return url;
    };

    // 数据概览
    const handleOverview = record => {
        dispatch(setCursor(record.id));

        setCurrentOrg(record);
        setCurrentRecordId(record.id);
        setOpenDataOverviewDrawer(true);
    };

    // 查看专家库人员
    const handleViewExperts = record => {
        dispatch(
            setExpertFilter({
                unitName: record.name,
                page: 1,
                size: 20,
            }),
        );
        dispatch(setExpertCursor(0));
        navigate('/dc/experts');
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 50,
        },
        {
            title: '名称',
            dataIndex: 'name',
            render: (_, row) => {
                return (
                    <div className="org-info">
                        <div className="org-info-content">
                            <div className="org-name">
                                <span>{row.name}</span>
                                {!!prepareLogoUrl(row.logo) && (
                                    <Popover placement="right" content={<img className="org-list-logo" src={prepareLogoUrl(row.logo)} alt={row.name} />}>
                                        <span className="org-logo-icon">
                                            <i className="bi bi-image"></i>
                                        </span>
                                    </Popover>
                                )}
                            </div>
                            {(row.medOwnership || row.medType || !!prepareMedLevelGrade(row)) && (
                                <div className="org-pops">
                                    {row.medOwnership && <span className="medOwnership">{row.medOwnership}</span>}
                                    {row.medType && <span className="medType">{row.medType}</span>}
                                    {prepareMedLevelGrade(row) && <span className="medLevelGrade">{prepareMedLevelGrade(row)}</span>}
                                </div>
                            )}
                            {!!prepareOrgAliases(row) && (
                                <div className="org-aliases">
                                    <Text type="secondary">{prepareOrgAliases(row)}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: '机构类型', // 机构类型（orgType）：0未分类，1医疗机构，2政府，3学协会，4科研院校，5企业
            dataIndex: 'orgType',
            width: 100,
            render: (_, row) => <>{orgTypeList.find(item => item.value === row.orgType)?.label || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '所在地',
            dataIndex: 'area',
            width: 160,
            render: (_, row) => <>{row.province ? dealArea(row) : <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '成立时间',
            dataIndex: 'establishedAt',
            width: 150,
            render: (_, row) => (
                <>
                    <div>{!!row.establishedAt ? formatEstablishedTime(row.establishedAt, row.establishedDateType) : <Text type="secondary">&ndash;</Text>}</div>
                    {!!row.yearsSinceEstablished && <div className="established-year">{dealYear(row.yearsSinceEstablished)}</div>}
                </>
            ),
        },
        {
            title: '专家库人员',
            dataIndex: 'area',
            width: 100,
            render: (_, row) => (
                <>
                    {row.expertCount ? (
                        <Button size="small" type="link" onClick={() => handleViewExperts(row)}>
                            {row.expertCount}人
                        </Button>
                    ) : (
                        <Text type="secondary">&ndash;</Text>
                    )}
                </>
            ),
        },
        {
            title: '审核状态',
            dataIndex: 'reviewStatus',
            width: 100,
            render: (_, row) => (
                <>
                    {row.reviewStatus === 0 && <Text type="warning">待审核</Text>}
                    {row.reviewStatus === 1 && <Text type="success">已通过</Text>}
                    {row.reviewStatus === 2 && <Text type="danger">已驳回</Text>}
                </>
            ),
        },
        {
            title: '来源',
            dataIndex: 'source',
            width: 180,
            render: (_, row) => (
                <>
                    <div>
                        {row.source === 0 ? (
                            '后台新增'
                        ) : (
                            <Popover
                                content={
                                    <>
                                        <span style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onCopy(row.memcard)}>
                                            {row.memcard}
                                        </span>
                                        <small>（点击复制）</small>
                                    </>
                                }
                            >
                                用户新增
                            </Popover>
                        )}
                    </div>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.createdAt}
                        </Text>
                    </div>
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 200,
            render: (_, row) => (
                <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                    <Tooltip title="查看、审核机构信息">
                        <Button size="small" type="link" onClick={() => handleView(row)}>
                            查看
                        </Button>
                    </Tooltip>
                    <Button size="small" type="link" onClick={() => handleEdit(row)}>
                        编辑
                    </Button>
                    {(row.orgType === 1 || row.orgType === 5) && (
                        <>
                            <Button size="small" type="link" onClick={() => handleOverview(row)}>
                                数据概览
                            </Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const orgSelection = {
        selectedRowKeys: selectedOrgsIds,
        preserveSelectedRowKeys: true,
        onChange: (selectedRowKeys, selectedRows) => {
            // 检查所选机构数量是否超过100个
            if (selectedRowKeys.length > 100) {
                message.warning('所选机构不能超过100个');
                return; // 不更新选择状态
            }

            if (selectedRows.length > 0) {
                setSelectedOrgsIds([...selectedRowKeys]);
            } else {
                setSelectedOrgsIds([]);
            }
        },
    };

    // 合并所选
    // - 把机构名称用,拼接后传递给机构合并页面
    // - 1种具体类型机构或未分类机构间可合并，不同类型机构间不可合并
    //     - 如，医疗机构间可合并，医疗机构和学协会间不可合并，医疗机构和未分类机构间可合并
    const handleMerge = () => {
        // 获取所选机构类型
        const typeList = selectedOrgsIds.map(item => tableData.find(row => row.id === item)?.orgType);
        // 过滤掉未分类（orgType=0）
        const filterTypeList = typeList.filter(item => !!item);
        // 判断是否存在多个机构类型
        const isMultiple = Array.from(new Set(filterTypeList)).length > 1;
        if (isMultiple) {
            message.warning('请选择相同机构类型合并');
        } else {
            if (selectedOrgsIds.length > 1) {
                // 如果选择的都是未分类机构，使用0；否则使用具体的机构类型
                const targetOrgType = filterTypeList.length > 0 ? filterTypeList[0] : 0;
                navigate('/dc/organization/merge', {
                    state: {
                        keyword: selectedOrgsIds.join(','),
                        orgType: targetOrgType,
                    },
                });
            } else {
                message.warning('请选择至少两个机构进行合并');
            }
        }
    };

    // 排序下拉菜单
    const onClickDropdown = ({ key }) => {
        dispatch(setFilter({ ...filter, orderby: Number(key) }));
    };

    // 获取机构列表
    const getOrganizationsList = useCallback(async () => {
        setLoading(true);
        const res = await organizationsListApi({ ...filter, allColumn: 0 }); // allColumn: 0 - 代表boss后台机构检索
        setLoading(false);
        if (res && res.status_code === 200 && res.data) {
            setTableData(res.data.records || []);
            setTotal(res.data.total);
        }
    }, [filter]);

    const getOrganizationsGroupCount = useCallback(async () => {
        const res = await organizationsGroupCountApi();
        if (res && res.status_code === 200 && res.data) {
            setGroupCount(res.data);
        }
    }, []);

    useEffect(() => {
        const newFilter = { ...filter };
        const code = [];
        if (filter.provinceCode) {
            code[0] = filter.provinceCode;
        }
        if (filter.cityCode) {
            code[1] = filter.cityCode;
        }
        if (filter.districtCode) {
            code[2] = filter.districtCode;
        }
        newFilter.areas = code;

        if (filter.established && filter.established !== -1 && filter.established !== -2) {
            newFilter.established = 0;
            newFilter.year = filter.established;
        }

        if (filter.keywordList && Array.isArray(filter.keywordList)) {
            newFilter.keywordList = filter.keywordList.join();
        }

        if (filter.featureList && Array.isArray(filter.featureList)) {
            newFilter.featureList = filter.featureList.join();
        }

        form.setFieldsValue(newFilter);
        getOrganizationsList();
    }, [filter, form, getOrganizationsList]);

    useEffect(() => {
        getOrganizationsGroupCount();
    }, [getOrganizationsGroupCount]);

    // 检索条件发生改变时，清空已选
    useEffect(() => {
        setSelectedOrgsIds([]);
    }, [filter]);

    // 机构类型统计链接点击
    // 审核通过机构，设置 filter 中的 orgType，审核状态 reviewStatus 为已通过（1），其他参数重置
    // 待审核机构，设置 filter 中的 orgType 为 undefined，审核状态 reviewStatus 为待审核（0），其他参数重置
    const handleSummaryStatisticsLink = (orgType, reviewStatus) => {
        dispatch(
            setFilter({
                orgType: orgType !== undefined ? [orgType] : undefined,
                reviewStatus,
                page: 1,
                size: 10,
            }),
        );
    };

    useEffect(() => {
        document.title = '机构管理-机构库';
    }, []);

    return (
        <div className="page-container list-page-container dc-organization-list-page">
            <PageHeader title="机构管理" breadcrumb={[{ label: '数据中心' }, { label: '机构库' }, { label: '机构管理' }]} {...filterToggleProps} />

            <div className="summary-statistics">
                <Row>
                    <Col xs={24} sm={24} md={18}>
                        <span>
                            审核通过的医疗机构
                            <Button type="link" size="small" onClick={() => handleSummaryStatisticsLink(1, 1)}>
                                {groupCount.medCount}
                            </Button>
                            家，
                        </span>
                        <span>
                            企业
                            <Button type="link" size="small" onClick={() => handleSummaryStatisticsLink(5, 1)}>
                                {groupCount.enterpriseCount}
                            </Button>
                            家，
                        </span>
                        <span>
                            学协会
                            <Button type="link" size="small" onClick={() => handleSummaryStatisticsLink(3, 1)}>
                                {groupCount.associationCount}
                            </Button>
                            家，
                        </span>
                        <span>
                            科研院校
                            <Button type="link" size="small" onClick={() => handleSummaryStatisticsLink(4, 1)}>
                                {groupCount.researchInstituteCount}
                            </Button>
                            家，
                        </span>
                        <span>
                            政府
                            <Button type="link" size="small" onClick={() => handleSummaryStatisticsLink(2, 1)}>
                                {groupCount.governmentCount}
                            </Button>
                            家
                        </span>
                    </Col>
                    <Col xs={24} sm={24} md={6} style={{ textAlign: 'right' }} className="summary-pending">
                        <span>
                            待审核机构
                            <Button type="link" size="small" onClick={() => handleSummaryStatisticsLink(undefined, 0)}>
                                {groupCount.reviewCount}
                            </Button>
                            家
                        </span>
                    </Col>
                </Row>
            </div>

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="organization-filter-box"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} onFinish={onFinish} form={form} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item
                                    {...filterFormItemLayout}
                                    label={
                                        <span className="form-item-label-text">
                                            名称/ID
                                            <Tooltip title="机构ID或名称、别称、简称、显示名称的关键字，多个ID或关键字使用英文逗号隔开">
                                                <i className="bi bi-question-circle-fill" style={{ marginLeft: 4, color: '#faad14' }}></i>
                                            </Tooltip>
                                        </span>
                                    }
                                    name="keywordList"
                                    style={filterFormItemStyle}
                                >
                                    <Input allowClear placeholder="多个关键词用英文逗号分隔" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item {...filterFormItemLayout} label="机构类型" name="orgType" style={filterFormItemStyle}>
                                    <Select placeholder="选择机构类型" allowClear mode="multiple">
                                        {orgTypeList
                                            && !!orgTypeList.length
                                            && orgTypeList.map(item => (
                                                <Select.Option key={item.value} value={item.value}>
                                                    {item.label}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            {orgType?.includes(1) && (
                                <>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item {...filterFormItemLayout} label="医疗机构性质" name="medOwnership" style={filterFormItemStyle}>
                                            <Select placeholder="选择医疗机构性质" allowClear>
                                                {medOwnershipList.map(item => (
                                                    <Select.Option value={item} key={item}>
                                                        {item}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item {...filterFormItemLayout} label="医疗机构类型" name="medType" style={filterFormItemStyle}>
                                            <Select placeholder="选择医疗机构类型" allowClear>
                                                {medTypeList.map(item => (
                                                    <Select.Option value={item} key={item}>
                                                        {item}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item {...filterFormItemLayout} label="医疗机构级别" name="medLevel" style={filterFormItemStyle}>
                                            <Select placeholder="选择医疗机构级别" allowClear>
                                                {medLevelList.map(item => (
                                                    <Select.Option value={item} key={item}>
                                                        {item}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item {...filterFormItemLayout} label="医疗机构等次" name="medGrade" style={filterFormItemStyle}>
                                            <Select placeholder="选择医疗机构等次" allowClear>
                                                {medGradeList.map(item => (
                                                    <Select.Option value={item} key={item}>
                                                        {item}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </>
                            )}

                            <Col {...filterFormGridLayout}>
                                <Form.Item {...filterFormItemLayout} label="所在地区" name="areas" style={filterFormItemStyle}>
                                    <Cascader placeholder="选择地区" options={regionOptions} changeOnSelect showSearch />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item
                                    {...filterFormItemLayout}
                                    label={
                                        <span className="form-item-label-text">
                                            成立周年
                                            <Tooltip title="机构成立时间按年份填写时，成立周年统一按照当年1月1日计算（如，按年份填写1984年，计算逻辑视为1984年1月1日成立，并倒推计算成立周年），同理，按月份填写时，统一按照当月1日计算。">
                                                <i className="bi bi-question-circle-fill" style={{ marginLeft: 4, color: '#faad14' }}></i>
                                            </Tooltip>
                                        </span>
                                    }
                                    className="organization-form-item"
                                    style={filterFormItemStyle}
                                >
                                    <div className={established === 0 && 'established-form-item'}>
                                        <Form.Item name="established">
                                            <Select placeholder="选择成立周年数" allowClear>
                                                <Select.Option value={-2}>将满周年数为10或5的倍数</Select.Option>
                                                <Select.Option value={-1}>已满周年数为10或5的倍数</Select.Option>
                                                <Select.Option value={0}>其他</Select.Option>
                                            </Select>
                                        </Form.Item>
                                        {established === 0 && (
                                            <Form.Item name="year" className="established-year">
                                                <Input allowClear placeholder="周年数" />
                                            </Form.Item>
                                        )}
                                    </div>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item {...filterFormItemLayout} label="审核状态" name="reviewStatus" style={filterFormItemStyle}>
                                    <Select placeholder="选择审核状态" allowClear>
                                        <Select.Option value={0}>待审核</Select.Option>
                                        <Select.Option value={1}>已通过</Select.Option>
                                        <Select.Option value={2}>已驳回</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item
                                    {...filterFormItemLayout}
                                    label={
                                        <span className="form-item-label-text">
                                            机构特色
                                            <Tooltip title="机构简介关键字或医疗机构专科，多个关键字或专科使用英文逗号隔开">
                                                <i className="bi bi-question-circle-fill" style={{ marginLeft: 4, color: '#faad14' }}></i>
                                            </Tooltip>
                                        </span>
                                    }
                                    name="featureList"
                                    style={filterFormItemStyle}
                                >
                                    <Input allowClear placeholder="多个关键词或专科用英文逗号分隔" />
                                </Form.Item>
                            </Col>
                            <Col {...buttonColLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" icon={<i className="bi bi-search" aria-hidden="true" />} htmlType="submit">
                                            检索
                                        </Button>
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container dc-organization-table-region">
                <div className="main-toolbar dc-organization-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={handleAddOrg}>
                                新增机构
                            </Button>
                            <Button type="primary" danger ghost onClick={handleMerge}>
                                合并所选{!!selectedOrgsIds.length && `(${selectedOrgsIds.length})`}
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Dropdown
                                menu={{
                                    items: [
                                        { key: 0, label: <>{!filter?.orderby ? <Link>按添加时间倒序 (默认)</Link> : '按添加时间倒序 (默认)'}</> },
                                        { key: 1, label: <>{filter?.orderby === 1 ? <Link>按添加时间正序</Link> : '按添加时间正序'}</> },
                                        { key: 2, label: <>{filter?.orderby === 2 ? <Link>按名称排序</Link> : '按名称排序'}</> },
                                    ],
                                    onClick: onClickDropdown,
                                }}
                            >
                                <Tooltip title="排序方式">
                                    <Button
                                        type="default"
                                        className={filter?.orderby ? 'sort-button-active' : ''}
                                        icon={
                                            <span className="anticon">
                                                <i className="bi bi-sort-down" style={{ fontSize: 16 }}></i>
                                            </span>
                                        }
                                        onClick={handleRefresh}
                                    />
                                </Tooltip>
                            </Dropdown>

                            <Tooltip title="刷新">
                                <Button
                                    type="default"
                                    icon={
                                        <span className="anticon">
                                            <i className="bi bi-arrow-clockwise" style={{ fontSize: 16 }}></i>
                                        </span>
                                    }
                                    onClick={handleRefresh}
                                />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap dc-organization-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        bordered
                        sticky
                        size="medium"
                        rowSelection={{ type: 'checkbox', ...orgSelection }}
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.size,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onShowSizeChange,
                        }}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>

            <AddOrgDrawer openAddOrgDrawer={openAddOrgDrawer} setOpenAddOrgDrawer={setOpenAddOrgDrawer} id={currentRecordId} getOrganizationsList={getOrganizationsList} setCurrentRecordId={setCurrentRecordId} setOpenViewOrgDrawer={setOpenViewOrgDrawer} />

            <ViewOrgDrawer openViewOrgDrawer={openViewOrgDrawer} setOpenViewOrgDrawer={setOpenViewOrgDrawer} id={currentRecordId} setOpenAddOrgDrawer={setOpenAddOrgDrawer} setCurrentRecordId={setCurrentRecordId} getOrganizationsList={getOrganizationsList} />

            <DataOverviewDrawer setOpenDataOverviewDrawer={setOpenDataOverviewDrawer} openDataOverviewDrawer={openDataOverviewDrawer} id={currentRecordId} currentOrg={currentOrg} />
        </div>
    );
};

export default OrganizationList;
